/**
 * ReplyInput - Reply form for message thread
 *
 * Uses React Query mutation for sending via API (not WebSocket)
 */

import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import Dropzone from 'react-dropzone';

import TextSnippetWrapper from 'components/TextSnippetWrapper';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import GlobalTooltip from 'components/GlobalTooltip';
import CustomMessageScheduleOverlayPanel from 'components/CutomMessageScheduleOverlayPanel/CustomMessageScheduleOverlayPanel';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { useMessageContext } from '../../context';
import { useSendReply } from '../../hooks/useMessageMutations';
import { messageQueryKeys } from '../../config/queryKeys';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { sanitizeHtmlColorContent, getTenantDetails } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import useFetchFiles from 'hooks/useFetchFiles';
import useAutomatedTime from 'hooks/useAutomatedTime';
import constants from 'constants/index';
import { ACCEPTED_FILE_TYPES } from '../../constants';
import './ReplyInput.scss';
import { uploadFilesV2, UPLOAD_CATEGORY } from 'utils/filesUtils';

function ReplyInput({ messageDetails, hasPermission, addUserHandler }) {
  const userContext = useUserDetailsContext();
  const { isConnected } = useWebSocketContext();
  const { isFromLead, isFromClientPortal } = useMessageContext();
  const { addToast } = useToast();
  const [tenantId] = getTenantDetails();
  const { onMountUnMount, onOperationBreak } = useAutomatedTime(messageDetails?.case?.case_id, messageDetails?.case?.case_name);

  const queryClient = useQueryClient();
  const threadId = messageDetails?.message_thread_id;

  const sendReplyMutation = useSendReply();
  const { files, addFiles, removeFile, clearFiles, uploadFiles, uploadProgress, isUploading } = useFileUpload();

  const [isReply, setIsReply] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const scheduleOverlayRef = useRef(null);
  const replyFormRef = useRef(null);

  useEffect(() => {
    if (!isReply) return;
    const timer = setTimeout(() => {
      replyFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 240);
    return () => clearTimeout(timer);
  }, [isReply]);

  // Automated time entry logic
  useEffect(() => {
    isReply &&
      !isFromClientPortal &&
      typeof onMountUnMount === 'function' &&
      onMountUnMount('message-reply', 'mounted', messageDetails?.case?.case_id);
    return () => {
      isReply && !isFromClientPortal && typeof onOperationBreak === 'function' && onOperationBreak();
    };
  }, [isReply]);

  const { files: fetchedExistingFiles, filesLoading } = useFetchFiles({
    caseId: messageDetails?.case?.case_id,
    allFiles: true,
    enabled: isReply && !!messageDetails?.case?.case_id,
  });

  const {
    handleSubmit,
    control,
    resetField,
    watch,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      replyMessage: '',
      existing_files: [],
    },
  });

  const watchedValues = watch();

  const writeAutoSaveToCache = useCallback(
    (draftValues) => {
      if (!threadId) return;
      queryClient.setQueryData(messageQueryKeys.threadDetails.byId(threadId), (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            auto_save_res: draftValues,
          },
        };
      });
    },
    [queryClient, threadId]
  );

  const existingFilesKey = useMemo(() => JSON.stringify(watchedValues.existing_files ?? []), [watchedValues.existing_files]);

  useEffect(() => {
    if (!isReply || !threadId) return;
    writeAutoSaveToCache({
      replyMessage: watchedValues.replyMessage || '',
      existing_files: watchedValues.existing_files || [],
    });
  }, [isReply, threadId, watchedValues.replyMessage, existingFilesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reply draft is embedded in the thread details response — no extra API call.
  const replyDraft = messageDetails?.auto_save_res ?? null;

  const { clearDraft } = useAutoSave({
    caseId: messageDetails?.case?.case_id,
    autoSaveType: 'reply',
    messageThreadId: messageDetails?.message_thread_id,
    getFormValues: getValues,
    formValues: watchedValues,
    isDirty,
    enabled: isReply,
  });

  // Restore reply draft from thread details when the reply form opens
  useEffect(() => {
    if (replyDraft && Object.keys(replyDraft).length > 0) {
      if (replyDraft.replyMessage) setValue('replyMessage', replyDraft.replyMessage);
      if (replyDraft.existing_files?.length) setValue('existing_files', replyDraft.existing_files);
      setIsReply(true);
    }
  }, [replyDraft]);

  const handleCancel = useCallback(() => {
    writeAutoSaveToCache(null);
    setIsReply(false);
    setScheduleData(null);
    setShowSchedulePanel(false);
    resetField('replyMessage');
    resetField('existing_files');
    clearFiles();
  }, []);

  const handleReply = useCallback(
    async (data) => {
      try {
        // Validate total file count (uploaded + existing)
        const existingFilesCount = data.existing_files?.length || 0;
        const uploadedFilesCount = files.length;
        const totalFiles = existingFilesCount + uploadedFilesCount;

        // if (totalFiles > 20) {
        //   addToast(false, toastConstant.toasterType.WARN, toastConstant.header.WARNING, toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED);
        //   return;
        // }

        // Upload files first
        const options = {
          caseId: messageDetails?.case?.case_id,
          category: UPLOAD_CATEGORY.MESSAGES,
          message_thread_id: messageDetails?.message_thread_id,
        };

        const uploadedAttachments = await uploadFilesV2(files, options);
        if (uploadedAttachments === null) return; // validation failed

        // Merge uploaded files with existing selected files
        let finalAttachments = uploadedAttachments || [];
        if (data.existing_files && data.existing_files.length > 0) {
          finalAttachments = [...finalAttachments, ...data.existing_files];
        }

        // Build payload using the new API schema
        const payload = {
          type: 'reply',
          tenant_id: tenantId,
          case_id: messageDetails?.case?.case_id,
          message_thread_id: messageDetails?.message_thread_id,
          message: data.replyMessage || '',
          attachment: finalAttachments,
          event_attachment: [],
          task_attachment: [],
          is_lead: messageDetails?.is_lead,
          case_name: messageDetails?.case?.case_name || '',
          ...(scheduleData?.datetime && {
            scheduled_send_at: scheduleData.datetime,
            scheduled_status: 'scheduled',
          }),
          ...(messageDetails?.scheduled_status === 'scheduled' &&
            messageDetails?.scheduled_send_at && {
              scheduled_send_at: messageDetails?.scheduled_send_at,
              scheduled_status: 'scheduled',
            }),
        };

        await sendReplyMutation.mutateAsync(payload);

        // Automated time entry logic
        !isFromClientPortal &&
          typeof onMountUnMount === 'function' &&
          onMountUnMount('message-reply', 'unmounted', messageDetails?.case?.case_id);

        const wasScheduled = Boolean(scheduleData?.datetime);
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          wasScheduled ? 'Reply scheduled successfully' : 'Reply sent successfully'
        );

        clearDraft();
        writeAutoSaveToCache(null);
        setIsReply(false);
        setScheduleData(null);
        setShowSchedulePanel(false);
        resetField('replyMessage');
        resetField('existing_files');
        clearFiles();
      } catch (err) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.message || 'Failed to send reply');
        setScheduleData(null);
        setShowSchedulePanel(false);
      }
    },
    [messageDetails, tenantId, isFromLead, files.length, scheduleData]
  );

  const canWrite = userContext.getPermission('messages') === constants.permissions.WRITE;

  // Handle file drop with max limit validation
  const handleFileDrop = useCallback(
    (acceptedFiles) => {
      const existingFilesCount = files.length;
      const newFilesCount = acceptedFiles.length;
      const totalFiles = existingFilesCount + newFilesCount;

      // if (totalFiles > 20) {
      //   addToast(
      //     false,
      //     toastConstant.toasterType.WARN,
      //     toastConstant.toasterType.WARNING,
      //     toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
      //   );
      //   return;
      // }

      addFiles(acceptedFiles);
    },
    [files.length, addFiles, addToast]
  );

  return (
    <div className="row mt-2 email-quill" role="region" aria-label="Reply to message">
      {isReply ? (
        <div className="reply-form-container">
          <div className="col-sm-12 mb-2">
            <Controller
              control={control}
              name="replyMessage"
              rules={{
                validate: (value) => {
                  const existingFiles = watch('existing_files') || [];
                  const hasAttachments = files.length > 0 || existingFiles.length > 0;

                  if (!hasAttachments && (!value || value.trim() === '')) {
                    return 'Please enter your message or attach a file';
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, value } }) => (
                <TextSnippetWrapper
                  value={sanitizeHtmlColorContent(value)}
                  type="editor"
                  onChange={onChange}
                  aria-label="Message reply editor"
                  autoFocus
                />
              )}
            />
            {errors?.replyMessage && <InputErrorMessage>{errors.replyMessage.message || 'Please enter your message'}</InputErrorMessage>}
          </div>

          {/* File attachments */}
          <div>
            <ul className="list-unstyled" aria-label="Attached files">
              {files.map((val, index) => (
                <div className="reply-file-item d-flex align-items-center" key={index}>
                  <i className="fas fa-paperclip me-2 mt-1" aria-hidden="true" />
                  <li className="text-break ms-1">{val?.name || 'Untitled file'}</li>
                  <i
                    className="icon-delete ms-3 pointer"
                    onClick={() => removeFile(val.path)}
                    aria-label={`Remove ${val?.name || 'file'}`}
                  />
                </div>
              ))}
            </ul>

            <Dropzone onDrop={handleFileDrop} accept={ACCEPTED_FILE_TYPES}>
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className="col-12 px-2 drag-drop-box d-flex flex-wrap justify-content-center align-items-center">
                  <section className="d-flex align-items-center">
                    <div>
                      <input {...getInputProps()} />
                      <p className="text-center mb-0">Click or drag file to this area to upload</p>
                    </div>
                  </section>
                  <p className="mb-0 d-flex align-items-center">
                    <i>or</i>
                  </p>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    className="w-100 text-center"
                  >
                    <Controller
                      name="existing_files"
                      control={control}
                      render={({ field: { name, value, onChange } }) => (
                        <MultiSelect
                          options={
                            filesLoading
                              ? []
                              : fetchedExistingFiles?.length === 0
                              ? [
                                  {
                                    label: 'No Records Found',
                                    value: 'no-records-found',
                                  },
                                ]
                              : fetchedExistingFiles.map((item) => ({
                                  label: item.display_name,
                                  value: {
                                    file_path: item?.file_path,
                                    size: item?.size,
                                    display_name: item?.display_name,
                                    file_name: item?.file_name,
                                    file_type: item?.file_type,
                                    created_by: item?.created_by_details,
                                    is_new_file: true,
                                    sk: item?.sk,
                                    pk: item?.pk,
                                  },
                                }))
                          }
                          className="input-shadow existing-files"
                          placeholder="Select Existing Files"
                          display="chip"
                          value={Array.isArray(value) ? value : []}
                          filter
                          onChange={(e) => {
                            const selectedValues = e.value;
                            const totalFiles = files?.length + selectedValues?.length;

                            // if (totalFiles > 20) {
                            //   addToast(
                            //     false,
                            //     toastConstant.toasterType.WARN,
                            //     toastConstant.toasterType.WARNING,
                            //     toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
                            //   );
                            //   return;
                            // }

                            onChange(selectedValues);
                          }}
                          disabled={!messageDetails?.case?.case_id || filesLoading}
                          optionLabel="label"
                          optionValue="value"
                          optionDisabled={(index) => index.value === 'no-records-found'}
                          resetFilterOnHide
                          id={name}
                          emptyFilterMessage="No records found"
                          showSelectAll={false}
                          dropdownIcon={filesLoading ? 'pi pi-spinner pi-spin' : undefined}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
            </Dropzone>
          </div>

          {/* Upload progress */}
          {isUploading && uploadProgress > 0 && (
            <div className="reply-progress-wrap mt-2">
              <div className="progress">
                <div
                  className="progress-bar reply-progress-bar"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div ref={replyFormRef} className="d-flex justify-content-end align-items-center mt-3">
            <div className="scheduled-button-group d-flex position-relative overflow-visible">
              <Button
                label="Cancel"
                className="reply-action-btn p-button p-button-secondary outline me-2"
                type="button"
                onClick={handleCancel}
              />

              {isFromClientPortal || (messageDetails?.scheduled_status === 'scheduled' && messageDetails?.scheduled_send_at) ? (
                <Button
                  label="Send"
                  className="reply-action-btn p-button p-button-secondary"
                  type="button"
                  disabled={sendReplyMutation.isPending || isUploading}
                  loading={sendReplyMutation.isPending || isUploading}
                  onClick={handleSubmit(handleReply)}
                />
              ) : (
                <div className="schedule-button-group">
                  <Button
                    type="button"
                    label={scheduleData ? 'Schedule' : 'Send'}
                    className="reply-action-btn p-button p-button-secondary schedule-main-btn"
                    disabled={sendReplyMutation.isPending || isUploading}
                    loading={sendReplyMutation.isPending || isUploading}
                    onClick={handleSubmit(handleReply)}
                  />
                  <div
                    className={`schedule-dropdown-btn${showSchedulePanel ? ' panel-open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowSchedulePanel((prev) => !prev);
                    }}
                  >
                    <div className="schedule-separator" />
                    <i className="pi pi-chevron-down schedule-chevron" />
                  </div>
                </div>
              )}

              {!isFromClientPortal && (
                <CustomMessageScheduleOverlayPanel
                  ref={scheduleOverlayRef}
                  visible={showSchedulePanel}
                  onHide={() => setShowSchedulePanel(false)}
                  style={{
                    position: 'absolute',
                    top: 'auto',
                    bottom: '100%',
                    right: '0',
                    zIndex: 1000,
                    marginBottom: '8px',
                  }}
                  scheduleData={scheduleData}
                  setScheduleData={setScheduleData}
                />
              )}
            </div>
          </div>

          {/* Scheduled time preview */}
          {scheduleData?.datetime && (
            <div className="reply-schedule-preview mt-2 f-size13 text-hint justify-content-end d-flex">
              Scheduled to send:{' '}
              {convertDateFromApiToUserTimezone(
                scheduleData.datetime,
                userContext?.userDetails?.timezone,
                constants.month_date_year_time_12_format
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="col-sm-12 text-end border-top pt-2">
          {canWrite && hasPermission && (
            <GlobalTooltip tooltip={hasPermission ? '' : 'You are not part of this thread'}>
              <Button
                label={isConnected ? 'Reply' : 'Connecting'}
                className="reply-action-btn btn-outline p-button-secondary py-1 px-3 mt-3"
                type="button"
                onClick={() => setIsReply(true)}
                disabled={!hasPermission || !isConnected}
                aria-label="Reply to this message"
                icon="pi pi-reply"
              />
            </GlobalTooltip>
          )}
          {canWrite && !hasPermission && (
            <GlobalTooltip tooltip="Add myself to this conversation" position="left">
              <Button
                label="Add"
                className="btn-outline p-button-secondary"
                type="button"
                onClick={addUserHandler}
                aria-label="Add myself to this conversation"
              />
            </GlobalTooltip>
          )}
        </div>
      )}
    </div>
  );
}

ReplyInput.propTypes = {
  messageDetails: PropTypes.shape({
    message_thread_id: PropTypes.string,
    case: PropTypes.shape({
      case_id: PropTypes.string,
      case_name: PropTypes.string,
    }),
  }),
  hasPermission: PropTypes.bool.isRequired,
  addUserHandler: PropTypes.func.isRequired,
};

ReplyInput.defaultProps = {
  messageDetails: null,
};

export default memo(ReplyInput);
